import { Filters } from '@/service-library/request-types'
import { Document, DocumentRow, DocumentRowValue } from '@/types/documents'
import {
  FilterDefinition,
  FilterField,
  FilterOp,
  FilterSource,
  LiveListConfig,
  LiveListConfigs,
  StoredFilter,
} from '@/types/live-list-filter'

/**
 * Ops whose value carries multiple entries. A list-valued source value is only
 * valid for one of these (transported as a comma-separated string the endpoint
 * splits again); any other op with a list value is a misconfiguration.
 */
const MULTI_VALUE_OPS = new Set<FilterOp>(['in', 'endswith-in'])

/** A value read from a doc/doc_row source before it is serialized to a param. */
type SourceValue = string | number | boolean | unknown[] | null

export type ResolveRowValue = (
  row: DocumentRow,
  infoTypeId: string,
) => DocumentRowValue | undefined

export type ResolveDocValue = (
  doc: Document,
  infoTypeId: string,
) => DocumentRowValue | undefined

export interface FilterBuilderOptions {
  storedFilter: StoredFilter | null
  infoTypeId: string
  liveListConfigs: LiveListConfigs
  /** Already-loaded in-memory entities. */
  document?: Document
  documentRow?: DocumentRow
  /**
   * Map of info_type_id -> project_grid_field_id, used by the default
   * value resolvers to find a DocumentRowValue. Required unless you
   * provide custom resolvers.
   */
  projectGridFieldIdByInfoType?: Record<string, string>
  /** Optional overrides (e.g. tests). */
  resolveRowValue?: ResolveRowValue
  resolveDocValue?: ResolveDocValue
  /** Extra keys merged into the final params (paging, cred ids, etc.). */
  extra: Filters | null
}

export interface FilterBuilderResult {
  readonly liveListConfig: LiveListConfig
  readonly definition: FilterDefinition
  readonly liveListUrl: string
  /** Returns the flat `{ field__op: value }` params object. */
  build(): Filters
}

// --- helpers ------------------------------------------------------------

function resolveLiveListConfig(
  infoTypeId: string,
  configs: LiveListConfigs,
): LiveListConfig {
  // Last one wins (matches the Python `configs_by_info_type` behavior).
  let match: LiveListConfig | undefined
  configs.live_list_configs.forEach((cfg) => {
    if (cfg.info_types.includes(infoTypeId)) match = cfg
  })
  if (!match) {
    throw new Error(`No LiveListConfig registered for info_type ${infoTypeId}`)
  }
  return match
}

function sourceOp(field: FilterField): FilterOp {
  // A source may pin the op explicitly (e.g. `in` for a list-valued attribute
  // like a phase's `cost_type_ids`); otherwise default to `eq` when available,
  // else the first declared op.
  const pinned = field.source?.op
  if (pinned) {
    // The backend's FilterField validator already guarantees this, but the
    // config is served over the wire — guard against a regression rather than
    // silently emitting a `field__op` key the API will reject.
    if (!field.ops.includes(pinned)) {
      throw new Error(
        `Field "${field.name}": pinned source op "${pinned}" is not in ops ` +
          `${[...field.ops].sort().join(', ')}`,
      )
    }
    return pinned
  }
  return field.ops.includes('eq') ? 'eq' : field.ops[0]
}

function extractAttr(
  value: DocumentRowValue | undefined,
  attribute: string,
): SourceValue {
  const json = value?.final_value_json
  if (!json) return null
  const v = json[attribute]
  return v === '' ? null : v ?? null
}

function defaultResolveRowValue(
  fieldByInfoType: Record<string, string>,
): ResolveRowValue {
  return (row, infoTypeId) => {
    const fieldId = fieldByInfoType[infoTypeId]
    if (!fieldId) return undefined
    return row.document_row_values?.find(
      (v) => v.project_grid_field_id === fieldId,
    )
  }
}

function defaultResolveDocValue(
  fieldByInfoType: Record<string, string>,
): ResolveDocValue {
  return (doc, infoTypeId) => {
    const fieldId = fieldByInfoType[infoTypeId]
    if (!fieldId) return undefined
    let hit: DocumentRowValue | undefined
    ;(doc.document_rows ?? []).some((row) => {
      hit = row.document_row_values?.find(
        (v) => v.project_grid_field_id === fieldId,
      )
      return !!hit
    })
    return hit
  }
}

export function validateStoredFilter(
  stored: StoredFilter,
  def: FilterDefinition,
): void {
  const allowed = new Map<string, Set<string>>()
  def.fields.forEach((f) => allowed.set(f.name, new Set(f.ops)))

  stored.clauses.forEach((c, i) => {
    const ops = allowed.get(c.field)
    if (!ops) {
      throw new Error(
        `Clause ${i}: unknown field "${c.field}" for resource ${def.resource}`,
      )
    }
    if (!ops.has(c.op)) {
      throw new Error(
        `Clause ${i}: op "${c.op}" not allowed for "${c.field}"; ` +
          `allowed: ${[...ops].sort().join(', ')}`,
      )
    }
  })
}

// --- main ---------------------------------------------------------------

export function createFilterBuilder(
  opts: FilterBuilderOptions,
): FilterBuilderResult {
  const liveListConfig = resolveLiveListConfig(
    opts.infoTypeId,
    opts.liveListConfigs,
  )
  const definition = liveListConfig.filter_config

  const storedFilter: StoredFilter = opts.storedFilter ?? {
    resource: definition.resource,
    resource_version: definition.version,
    schema_version: 1,
    clauses: [],
  }

  const fieldMap = opts.projectGridFieldIdByInfoType ?? {}
  const resolveRow: ResolveRowValue =
    opts.resolveRowValue ?? defaultResolveRowValue(fieldMap)
  const resolveDoc: ResolveDocValue =
    opts.resolveDocValue ?? defaultResolveDocValue(fieldMap)

  function validateEntityRequirements(): void {
    const needsRow = definition.fields.some(
      (f) => f.source?.scope === 'doc_row',
    )
    const needsDoc = definition.fields.some((f) => f.source?.scope === 'doc')

    if (needsRow && !opts.documentRow) {
      throw new Error(
        `Resource "${definition.resource}" has doc_row-scoped ` +
          `FilterSources; \`documentRow\` is required`,
      )
    }
    if (needsDoc && !opts.document && !opts.documentRow?.document) {
      throw new Error(
        `Resource "${definition.resource}" has doc-scoped ` +
          `FilterSources; \`document\` (or \`documentRow.document\`) is required`,
      )
    }
  }

  function readFirstFromRow(
    infoTypes: string[],
    attribute: string,
  ): SourceValue {
    const row = opts.documentRow as DocumentRow
    let result: SourceValue = null
    infoTypes.some((it) => {
      const value = extractAttr(resolveRow(row, it), attribute)
      if (value != null) {
        result = value
        return true
      }
      return false
    })
    return result
  }

  function readFirstFromDoc(
    infoTypes: string[],
    attribute: string,
  ): SourceValue {
    const doc = opts.document ?? opts.documentRow?.document
    if (!doc) return null
    let result: SourceValue = null
    infoTypes.some((it) => {
      const value = extractAttr(resolveDoc(doc, it), attribute)
      if (value != null) {
        result = value
        return true
      }
      return false
    })
    return result
  }

  function resolveSourceValue(source: FilterSource): SourceValue {
    if (source.scope === 'doc_row') {
      return readFirstFromRow(source.info_types, source.attribute)
    }
    if (source.scope === 'doc') {
      return readFirstFromDoc(source.info_types, source.attribute)
    }
    throw new Error(`Unsupported FilterSource scope: ${source.scope}`)
  }

  function build(): Filters {
    validateStoredFilter(storedFilter, definition)

    const params: Filters = {}

    // 1. Stored clauses
    storedFilter.clauses.forEach((clause) => {
      params[`${clause.field}__${clause.op}`] = String(clause.value)
    })

    // 2. Source-derived fields
    definition.fields.forEach((field) => {
      if (!field.source) return
      const op = sourceOp(field)
      const key = `${field.name}__${op}`
      if (key in params) return // caller-set wins

      const value = resolveSourceValue(field.source)
      if (value == null || value === '') {
        if (field.source.required) {
          throw new Error(
            `Required source value missing for field "${field.name}" ` +
              `(resource=${definition.resource})`,
          )
        }
        return
      }
      // A list-valued source (e.g. a phase's cost_type_ids) is only valid for a
      // multi-value op, transported as a comma-separated string the endpoint
      // splits again. Any other op with a list value is a config error (a
      // mis-pinned `source.op`) — fail fast rather than silently emitting a
      // wrong query.
      if (Array.isArray(value)) {
        if (!MULTI_VALUE_OPS.has(op)) {
          throw new Error(
            `Field "${field.name}": source produced a list value but op ` +
              `"${op}" is not multi-valued (resource=${definition.resource}); ` +
              `pin source.op to one of ${[...MULTI_VALUE_OPS]
                .sort()
                .join(', ')}`,
          )
        }
        params[key] = value.map((v) => String(v)).join(',')
        return
      }
      params[key] = String(value)
    })

    // 3. Caller extras
    if (opts.extra) Object.assign(params, opts.extra)

    return params
  }

  validateEntityRequirements()

  return {
    liveListConfig,
    definition,
    liveListUrl: liveListConfig.live_list_url,
    build,
  }
}
